import React, { useState } from "react";
import { Sparkles, MessageCircle } from "lucide-react";
import { submitCustomBouquet } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { toast } from "sonner";

const FLOWERS = ["Rose", "Sunflower", "Tulip", "Daisy", "Lily", "Lotus", "Jasmine", "Orchid"];
const WRAPPERS = ["Blush Pink", "Cream", "Kraft", "Black Luxe", "White Silk"];
const SIZES = [
  { id: "standard", name: "Standard", base: 799, desc: "5-8 flowers" },
  { id: "jumbo", name: "Jumbo", base: 1499, desc: "12-18 flowers" },
];

export const CustomBouquetPage = () => {
  const [size, setSize] = useState("standard");
  const [flowers, setFlowers] = useState([]);
  const [wrapper, setWrapper] = useState("Blush Pink");
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  const baseObj = SIZES.find(s => s.id === size);
  const estimate = baseObj.base + flowers.length * 90;

  const toggle = (f) => setFlowers(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);

  const submit = async () => {
    if (!flowers.length) { toast.error("Please pick at least one flower"); return; }
    if (!name || !phone) { toast.error("Please add your name & phone"); return; }
    try {
      const res = await submitCustomBouquet({ size, flowers, wrapper, note });
      const msg = `🌸 *Custom Bouquet Request — Sugandhi Flowers*\n\n*Name:* ${name}\n*Phone:* ${phone}\n*Size:* ${baseObj.name} (${baseObj.desc})\n*Flowers:* ${flowers.join(", ")}\n*Wrapper:* ${wrapper}\n*Note:* ${note || "-"}\n\n*Estimated Price:* ₹${res.estimated_price.toLocaleString("en-IN")}\n\nPlease confirm the design & final price 💐`;
      openWhatsApp(msg);
      toast.success("Sent via WhatsApp!");
    } catch { toast.error("Something went wrong"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-24 md:pb-16" data-testid="custom-bouquet-page">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FCE4EC] rounded-full text-[#D81B60] text-xs font-bold">
          <Sparkles size={14} /> Design your own
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D0A22] mt-3">Custom Bouquet Studio</h1>
        <p className="text-[#8E6377] mt-2">Pick your size, flowers, wrap & note — we'll craft it just for you.</p>
      </div>

      <div className="bg-white rounded-3xl shadow-[0_10px_40px_rgba(216,27,96,0.08)] p-6 md:p-8 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#D81B60] font-bold mb-3">1. Choose Size</p>
          <div className="grid grid-cols-2 gap-3">
            {SIZES.map(s => (
              <button key={s.id} data-testid={`size-${s.id}`} onClick={() => setSize(s.id)}
                className={`p-4 rounded-2xl border-2 text-left transition-all ${size === s.id ? "border-[#D81B60] bg-[#FCE4EC]" : "border-[#F4D8E2] bg-white hover:border-[#F8BBD0]"}`}>
                <p className="font-serif text-lg text-[#2D0A22]">{s.name}</p>
                <p className="text-xs text-[#8E6377]">{s.desc}</p>
                <p className="text-sm font-bold text-[#D81B60] mt-1">from ₹{s.base}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-widest text-[#D81B60] font-bold mb-3">2. Select Flowers</p>
          <div className="flex flex-wrap gap-2">
            {FLOWERS.map(f => (
              <button key={f} data-testid={`flower-${f.toLowerCase()}`} onClick={() => toggle(f)}
                className={`px-4 py-2 rounded-full border transition-all text-sm font-medium ${flowers.includes(f) ? "bg-[#D81B60] border-[#D81B60] text-white" : "border-[#F4D8E2] text-[#2D0A22] hover:border-[#D81B60]"}`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-widest text-[#D81B60] font-bold mb-3">3. Wrapper Color</p>
          <div className="flex flex-wrap gap-2">
            {WRAPPERS.map(w => (
              <button key={w} data-testid={`wrapper-${w.toLowerCase().replace(/\s/g, '-')}`} onClick={() => setWrapper(w)}
                className={`px-4 py-2 rounded-full border transition-all text-sm font-medium ${wrapper === w ? "bg-[#D81B60] border-[#D81B60] text-white" : "border-[#F4D8E2] text-[#2D0A22] hover:border-[#D81B60]"}`}>
                {w}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-widest text-[#D81B60] font-bold mb-3">4. Personal Note (optional)</p>
          <textarea data-testid="custom-note" value={note} onChange={e => setNote(e.target.value)} rows={3}
            placeholder="e.g. Happy Anniversary Priya! — From Aditya"
            className="w-full px-4 py-3 rounded-2xl border border-[#F4D8E2] bg-white text-sm outline-none focus:border-[#D81B60] resize-none" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input data-testid="custom-name" value={name} onChange={e => setName(e.target.value)} placeholder="Your Name" className="px-4 py-3 rounded-2xl border border-[#F4D8E2] bg-white text-sm outline-none focus:border-[#D81B60]" />
          <input data-testid="custom-phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone" className="px-4 py-3 rounded-2xl border border-[#F4D8E2] bg-white text-sm outline-none focus:border-[#D81B60]" />
        </div>

        <div className="p-4 rounded-2xl bg-gradient-to-br from-[#FCE4EC] to-[#F8BBD0] flex items-center justify-between">
          <div>
            <p className="text-xs text-[#8E6377]">Estimated Price</p>
            <p className="text-2xl font-bold text-[#AD1457]" data-testid="custom-estimate">₹{estimate.toLocaleString("en-IN")}</p>
          </div>
          <button data-testid="submit-custom-button" onClick={submit}
            className="py-3 px-5 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold flex items-center gap-2 shadow-[0_4px_14px_rgba(37,211,102,0.3)]">
            <MessageCircle size={18} /> Send Request
          </button>
        </div>
      </div>
    </div>
  );
};
